import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { Permission } from '@/auth/permissions';
import { useAuth } from '@/auth/useAuth';
import { SpriteIcon } from '@/components/SpriteIcon';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  useCreateRole,
  useDeleteRole,
  useRoles,
  useUpdateRole,
  type RoleListItem,
} from './useRoles';

/**
 * Replaces `Pages/Auth/Roles.cshtml` at `/auth/roles`.
 *
 * Not a `<DataGrid>`: roles are a fixed handful ordered by `DisplayOrder`, with
 * no paging, sorting or filtering in the Razor original. A plain table is what
 * that page rendered and what this keeps.
 */

/** Long descriptions are clipped in the cell and shown whole on hover. */
const DESCRIPTION_CLIP = 50;

interface RoleFormValues {
  name: string;
  description: string;
  displayOrder: number;
}

const EMPTY: RoleFormValues = { name: '', description: '', displayOrder: 10 };

function errorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const problem = error.problem as { errors?: string[] } | null;
  return Array.isArray(problem?.errors) && problem.errors.length > 0
    ? problem.errors.join(' ')
    : error.message;
}

export function RolesPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const confirm = useConfirm();

  const can = {
    edit: hasPermission(Permission.roles.edit),
    delete: hasPermission(Permission.roles.delete),
    managePermissions: hasPermission(Permission.roles.managePermissions),
  };

  const { data, isLoading, isError } = useRoles();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();

  const [editing, setEditing] = useState<RoleListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [values, setValues] = useState<RoleFormValues>(EMPTY);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    setValues(
      editing
        ? {
            name: editing.name,
            description: editing.description,
            displayOrder: editing.displayOrder,
          }
        : EMPTY,
    );
  }, [editing, creating]);

  const closeForm = useCallback(() => {
    setCreating(false);
    setEditing(null);
    createRole.reset();
    updateRole.reset();
  }, [createRole, updateRole]);

  const handleSubmit = useCallback(() => {
    if (editing) {
      updateRole.mutate(
        { id: editing.id, description: values.description, displayOrder: values.displayOrder },
        { onSuccess: closeForm },
      );
    } else {
      createRole.mutate(values, { onSuccess: closeForm });
    }
  }, [editing, values, updateRole, createRole, closeForm]);

  const handleDelete = useCallback(
    async (role: RoleListItem) => {
      setDeleteError(null);

      const confirmed = await confirm({
        variant: 'danger',
        icon: 'trash-2',
        title: t('crud:Crud.Delete.ConfirmTitle', 'Confirm deletion'),
        message: t('Roles.ConfirmDelete', 'Delete the role {{name}}?', { name: role.name }),
        confirmLabel: t('Button.Delete', 'Delete'),
      });
      if (!confirmed) return;

      deleteRole.mutate(role.id, {
        // The server refuses system roles and roles that still have members;
        // surface its reason rather than a generic failure. It stays on the page
        // rather than in the dialog: `errorMessage` reads a shape only this
        // controller returns, and the alert above the table is where the page
        // already reports it.
        onError: (error) => setDeleteError(errorMessage(error)),
      });
    },
    [confirm, deleteRole, t],
  );

  const formError = errorMessage(createRole.error ?? updateRole.error);
  const stats = data?.statistics;

  if (isError) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {t('Roles.LoadError', 'Could not load roles. Please try again.')}
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h3 mb-1 d-flex align-items-center">
            <SpriteIcon name="shield" size={24} className="sa-icon me-2" />
            {t('Roles.Title', 'Roles')}
          </h1>
          <p className="text-muted mb-0">
            {t('Roles.Subtitle', 'Roles group the permissions an account is granted.')}
          </p>
        </div>
        <div className="d-flex gap-2">
          {can.managePermissions && (
            <Link
              to="/auth/rolepermissions"
              className="btn btn-outline-primary d-inline-flex align-items-center"
            >
              <SpriteIcon name="key" size={16} className="sa-icon me-2" />
              {t('Roles.ManagePermissions', 'Manage permissions')}
            </Link>
          )}
          <button
            type="button"
            className="btn btn-primary d-inline-flex align-items-center"
            onClick={() => setCreating(true)}
          >
            <SpriteIcon name="plus" size={16} className="sa-icon me-2" />
            {t('Roles.Create', 'Add role')}
          </button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {[
          { key: 'total', label: t('Roles.Total', 'Total roles'), value: stats?.total, tone: '' },
          { key: 'system', label: t('Roles.System', 'System roles'), value: stats?.system, tone: 'text-primary' },
          { key: 'custom', label: t('Roles.Custom', 'Custom roles'), value: stats?.custom, tone: 'text-info' },
        ].map((tile) => (
          <div className="col-12 col-md-4" key={tile.key}>
            <div className="card shadow-sm h-100">
              <div className="card-body text-center">
                <h5 className="text-muted mb-2 fs-sm">{tile.label}</h5>
                <h2 className={`mb-0 ${tile.tone}`}>{tile.value ?? '—'}</h2>
              </div>
            </div>
          </div>
        ))}
      </div>

      {deleteError && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {deleteError}
          <button type="button" className="btn-close" onClick={() => setDeleteError(null)} />
        </div>
      )}

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="thead-themed">
              <tr>
                <th>{t('Roles.Name', 'Role name')}</th>
                <th>{t('Roles.Description', 'Description')}</th>
                <th>{t('Roles.Users', 'Users')}</th>
                <th>{t('Roles.Permissions', 'Permissions')}</th>
                <th>{t('Roles.DisplayOrder', 'Display order')}</th>
                <th>{t('Roles.Type', 'Type')}</th>
                <th className="text-end">{t('Actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    {t('Loading', 'Loading...')}
                  </td>
                </tr>
              )}

              {!isLoading && (data?.roles.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-muted">
                    {t('Roles.Empty', 'No roles defined')}
                  </td>
                </tr>
              )}

              {data?.roles.map((role) => (
                <tr key={role.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <SpriteIcon name="shield" size={20} className="sa-icon text-primary me-2" />
                      <strong>{role.name}</strong>
                    </div>
                  </td>
                  <td>
                    {role.description ? (
                      <span title={role.description}>
                        {role.description.length > DESCRIPTION_CLIP
                          ? `${role.description.substring(0, DESCRIPTION_CLIP)}…`
                          : role.description}
                      </span>
                    ) : (
                      <span className="text-muted">{t('Roles.NoDescription', 'No description')}</span>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-info">
                      {t('Roles.UserCount', '{{count}} users', { count: role.userCount })}
                    </span>
                  </td>
                  <td>
                    <span className="badge bg-secondary">{role.permissionCount}</span>
                  </td>
                  <td>{role.displayOrder}</td>
                  <td>
                    {role.isSystemRole ? (
                      <span className="badge bg-primary">{t('Roles.SystemRole', 'System')}</span>
                    ) : (
                      <span className="badge bg-secondary-subtle text-body-emphasis">
                        {t('Roles.CustomRole', 'Custom')}
                      </span>
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group btn-group-sm">
                      {can.edit && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setEditing(role)}
                          title={t('Roles.Edit', 'Edit role')}
                          aria-label={t('Roles.Edit', 'Edit role')}
                        >
                          <SpriteIcon name="edit" size={14} />
                        </button>
                      )}
                      {can.managePermissions && (
                        <Link
                          to="/auth/rolepermissions"
                          state={{ role: role.name }}
                          className="btn btn-outline-info"
                          title={t('Roles.ManagePermissions', 'Manage permissions')}
                          aria-label={t('Roles.ManagePermissions', 'Manage permissions')}
                        >
                          <SpriteIcon name="key" size={14} />
                        </Link>
                      )}
                      {/* A system role shows a padlock where the delete would be:
                          it cannot be removed, and the server refuses it too. */}
                      {can.delete &&
                        (role.isSystemRole ? (
                          <span
                            className="btn btn-outline-secondary disabled"
                            title={t('Roles.SystemRoleLocked', 'System roles cannot be deleted')}
                            aria-label={t('Roles.SystemRoleLocked', 'System roles cannot be deleted')}
                          >
                            <SpriteIcon name="lock" size={14} />
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => void handleDelete(role)}
                            disabled={deleteRole.isPending}
                            title={t('Roles.Delete', 'Delete role')}
                            aria-label={t('Roles.Delete', 'Delete role')}
                          >
                            <SpriteIcon name="trash-2" size={14} />
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && (
        <Modal show onHide={closeForm} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h5">
              {editing ? t('Roles.Edit', 'Edit role') : t('Roles.Create', 'Add role')}
            </Modal.Title>
          </Modal.Header>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
          >
            <Modal.Body>
              {formError && (
                <div className="alert alert-danger" role="alert">
                  {formError}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label" htmlFor="role-name">
                  {t('Roles.Name', 'Role name')} <span className="text-danger">*</span>
                </label>
                <input
                  id="role-name"
                  className="form-control"
                  value={values.name}
                  onChange={(event) => setValues((v) => ({ ...v, name: event.target.value }))}
                  placeholder={t('Roles.NamePlaceholder', 'e.g. Manager, Operator')}
                  required
                  // Renaming would detach every [Authorize(Roles = "...")] and
                  // every RequiredRoles menu row that names it.
                  disabled={editing != null}
                />
                {editing && (
                  <div className="form-text">
                    {t('Roles.NameLocked', 'A role cannot be renamed once it is in use.')}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="role-description">
                  {t('Roles.Description', 'Description')}
                </label>
                <textarea
                  id="role-description"
                  className="form-control"
                  rows={3}
                  value={values.description}
                  onChange={(event) =>
                    setValues((v) => ({ ...v, description: event.target.value }))
                  }
                  placeholder={t(
                    'Roles.DescriptionPlaceholder',
                    'Describe the purpose and responsibilities of this role',
                  )}
                />
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="role-order">
                  {t('Roles.DisplayOrder', 'Display order')}
                </label>
                <input
                  id="role-order"
                  type="number"
                  min={1}
                  max={1000}
                  className="form-control"
                  value={values.displayOrder}
                  onChange={(event) =>
                    setValues((v) => ({ ...v, displayOrder: Number(event.target.value) }))
                  }
                />
              </div>
            </Modal.Body>

            <Modal.Footer>
              <button type="button" className="btn btn-secondary" onClick={closeForm}>
                {t('Cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary d-inline-flex align-items-center"
                disabled={createRole.isPending || updateRole.isPending}
              >
                <SpriteIcon name="save" size={16} className="sa-icon me-2" />
                {t('Save', 'Save')}
              </button>
            </Modal.Footer>
          </form>
        </Modal>
      )}
    </div>
  );
}
